import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableContainer,
  useMaterialReactTable,
} from 'material-react-table';
import { Controller, useForm } from 'react-hook-form';
import {
  ADD_FIND_GROUP_COLLABORATOR_MUTATION,
  GET_FIND_GROUP_QUERY,
  GET_MY_CONTACTS_QUERY,
  REMOVE_FIND_GROUP_COLLABORATOR_MUTATION,
} from '../../gql';
import { formatDate } from '../../helpers';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../i18n';
import {
  Button,
  IconButton,
  SurfaceAutocomplete,
  SurfaceSelect,
} from '../../components/design-system';

interface Person {
  displayName?: string | null;
  email: string;
  createdAt: string;
  level: number;
  userId: string;
}

interface Contact {
  id: string;
  email: string;
  displayName?: string | null;
  responderUser: string;
}

const gridStyles = { display: 'grid', padding: 2 };
const inputStyles = { width: 280, padding: 0 };
const levelInputStyles = { width: 100, padding: 0 };
const levels = [0, 1, 2];
type AccessFormValues = { collaborator: Contact | null; level: number };
const defaultValues: AccessFormValues = { collaborator: null, level: 1 };

const getContactOptionLabel = (contact: Contact) =>
  `${contact.email}${contact.displayName ? ` (${contact.displayName})` : ''}`;

const isContactOptionEqual = (option: Contact, value: Contact) =>
  option.id === value.id;

export function FindGroupAccess({
  groupId,
  collaborators,
  readOnly,
}: {
  groupId: string;
  collaborators: Person[];
  readOnly: boolean;
}) {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo<MRT_ColumnDef<Person>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: intl.formatMessage({
          id: 'common.name',
          defaultMessage: 'Name',
        }),
      },
      { accessorKey: 'email', header: 'E-mail' },
      {
        accessorKey: 'level',
        header: intl.formatMessage({
          id: 'access.level',
          defaultMessage: 'Level',
        }),
      },
      {
        Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
        accessorFn: (row) => row.createdAt && new Date(row.createdAt),
        header: intl.formatMessage({
          id: 'access.addedOn',
          defaultMessage: 'Added on',
        }),
        size: 120,
      },
    ],
    [intl]
  );
  const { data: contactsData } = useQuery(GET_MY_CONTACTS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const contacts: Contact[] = contactsData?.getMyContacts?.contacts ?? [];
  const activeUserIds = collaborators.map(
    (collaborator) => collaborator.userId
  );
  const availableContacts = contacts.filter(
    (contact) => !activeUserIds.includes(contact.responderUser)
  );
  const refetchQueries = [
    { query: GET_FIND_GROUP_QUERY, variables: { id: groupId } },
  ];
  const [
    addCollaborator,
    { loading: adding },
  ] = useMutation(ADD_FIND_GROUP_COLLABORATOR_MUTATION, { refetchQueries });
  const [
    removeCollaborator,
  ] = useMutation(REMOVE_FIND_GROUP_COLLABORATOR_MUTATION, { refetchQueries });
  const { control, handleSubmit, reset, watch } = useForm<AccessFormValues>({
    defaultValues,
  });
  const selectedContact = watch('collaborator');

  const handleRemove = async (person: Person) => {
    if (
      window.confirm(
        intl.formatMessage(
          {
            id: 'finds.group.access.confirmRemove',
            defaultMessage:
              'Are you sure you want to stop sharing this group with {email}?',
          },
          { email: person.email }
        )
      )
    ) {
      await removeCollaborator({
        variables: { groupId, userId: person.userId },
      });
    }
  };
  const renderRowActions = ({ row }: { row: MRT_Row<Person> }) => (
    <Box sx={{ display: 'flex', gap: '1rem' }}>
      <IconButton
        onClick={() => handleRemove(row.original)}
        disabled={readOnly}
        density="compact"
        tone="danger"
        variant="ghost"
        aria-label={intl.formatMessage({
          id: 'access.removeCollaborator',
          defaultMessage: 'Remove collaborator',
        })}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
  const table = useMaterialReactTable({
    columns,
    data: collaborators,
    localization,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableColumnActions: false,
    enableColumnFilters: false,
    renderRowActions,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'compact',
    },
  });

  const onSubmit = async (values: AccessFormValues) => {
    await addCollaborator({
      variables: {
        groupId,
        userId: values.collaborator?.responderUser,
        level: values.level,
      },
    });
    reset({ collaborator: null, level: values.level });
  };

  return (
    <Paper sx={gridStyles}>
      {!readOnly && (
        <Stack
          gap={2}
          direction={{ xs: 'column', sm: 'row' }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          alignItems="center"
        >
          <Controller
            name="collaborator"
            control={control}
            render={({ field: { ref, onChange, ...field } }) => {
              const textFieldProps = { inputRef: ref };
              const handleChange = (_event: unknown, value: Contact | null) =>
                onChange(value);

              return (
                <SurfaceAutocomplete
                  {...field}
                  disablePortal
                  id="find-group-collaborator-user"
                  options={availableContacts}
                  disabled={availableContacts.length === 0}
                  isOptionEqualToValue={isContactOptionEqual}
                  getOptionLabel={getContactOptionLabel}
                  onChange={handleChange}
                  surface="sunken"
                  textFieldProps={textFieldProps}
                  sx={inputStyles}
                />
              );
            }}
          />
          <Typography variant="body2">
            <FormattedMessage
              id="access.withLevel"
              defaultMessage="with level:"
            />
          </Typography>
          <Controller
            name="level"
            control={control}
            render={({ field }) => (
              <SurfaceSelect
                {...field}
                id="find-group-collaborator-level"
                surface="sunken"
                sx={levelInputStyles}
              >
                {levels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </SurfaceSelect>
            )}
          />
          <Button disabled={adding || !selectedContact} type="submit">
            <FormattedMessage
              id="access.addCollaborator"
              defaultMessage="Add collaborator"
            />
          </Button>
        </Stack>
      )}
      <Typography mt={1} mb={2} variant="body2" p="4px">
        <FormattedMessage
          id="finds.group.access.levelHelp"
          defaultMessage="Level 0 is read-only, Level 1 can add finds, and Level 2 can edit finds in this group."
        />
      </Typography>
      <MRT_TableContainer table={table} />
    </Paper>
  );
}
